import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { Box, GlobalStyles, TablePagination } from '@mui/material';
import { agGridThemeLight, agGridThemeDark, agGridLocaleFr } from '../../styles/agGridTheme';

// Default per-cell tooltip: shows the cell's underlying value on hover so nothing
// gets lost to a truncated/ellipsized column. Applied to every column of every grid
// via `defaultColDef` below — individual column defs can still set their own
// `tooltipValueGetter` (or `tooltipValueGetter: undefined` to opt out) to override this.
const defaultTooltipValueGetter = (params) => {
  const { value } = params;
  if (value === null || value === undefined || value === '') return null;
  // Objects (e.g. a nested category/store object passed straight through as the
  // cell value) don't stringify usefully — skip rather than show "[object Object]".
  if (typeof value === 'object') return null;
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (value instanceof Date) return value.toLocaleString();
  return String(value);
};

/**
 * Thin wrapper around AgGridReact carrying Invotrix's shared conventions:
 *  - theme follows the app's light/dark mode (redux `theme.mode`)
 *  - French locale text
 *  - autoHeight layout + pagination defaults matching the old MUI DataGrid usage
 *  - a hover tooltip on every cell showing its full value (see
 *    defaultTooltipValueGetter above)
 *
 * Props map closely to the old @mui/x-data-grid ones so callers stay familiar:
 *  - rows          → rowData
 *  - columns       → columnDefs
 *  - loading       → loading
 *  - pageSize / pageSizeOptions → paginationPageSize / paginationPageSizeSelector
 *  - getRowId      → getRowId (ag-grid receives { data } instead of the row directly)
 *
 * Server mode (`paginationMode="server"`) — `rows` is just the CURRENT page, and paging
 * and sorting are delegated to the caller (typically useDataTable, which keeps them in the
 * URL). Extra props in this mode:
 *  - rowCount                 → total rows across all pages
 *  - paginationModel          → { page (0-indexed), pageSize }
 *  - onPaginationModelChange  → ({ page, pageSize }) => void
 *  - sortModel                → [{ field, sort: 'asc' | 'desc' }] (currently applied sort)
 *  - onSortModelChange        → (sortModel) => void
 * ag-grid's own client-side sorting/pagination are switched off so rows stay in the
 * order the server returned them; the header sort arrows only report the user's intent.
 */
// Every grid dims rows whose entity has `isActive === false` (gray + reduced
// opacity) — inactive entities stay listed in admin, they just look disabled.
// Callers can still pass their own `getRowClass` to override.
const defaultGetRowClass = (params) => (params.data?.isActive === false ? 'row-inactive' : undefined);

const inactiveRowStyles = (
  <GlobalStyles
    styles={{
      '.ag-theme-invotrix-light .ag-row.row-inactive': {
        opacity: 0.5,
        backgroundColor: '#f5f5f5 !important',
        color: '#9e9e9e !important',
      },
      '.ag-theme-invotrix-light .ag-row.row-inactive:hover': { backgroundColor: '#eeeeee !important' },
      '.ag-theme-invotrix-dark .ag-row.row-inactive': {
        opacity: 0.5,
        backgroundColor: 'rgba(255, 255, 255, 0.04) !important',
        color: '#9e9e9e !important',
      },
      '.ag-theme-invotrix-dark .ag-row.row-inactive:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08) !important' },
    }}
  />
);

// Keeps the server's row order: a comparator that never reorders.
const noopComparator = () => 0;

const paginationLabels = {
  labelRowsPerPage: 'Lignes par page :',
  labelDisplayedRows: ({ from, to, count }) => `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`,
};

const AppDataGrid = React.forwardRef(function AppDataGrid(
  {
    rows,
    columns,
    loading = false,
    pageSize = 10,
    pageSizeOptions = [10, 25, 50],
    getRowId,
    domLayout = 'autoHeight',
    sx,
    paginationMode = 'client',
    rowCount = 0,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    onGridReady,
    onSortChanged,
    ...rest
  },
  ref
) {
  const { mode } = useSelector((state) => state.theme);
  const server = paginationMode === 'server';

  // Internal api handle (the forwarded ref may be absent or a callback ref).
  const gridApiRef = useRef(null);
  const setRefs = useCallback(
    (instance) => {
      if (typeof ref === 'function') ref(instance);
      else if (ref) ref.current = instance;
    },
    [ref]
  );

  const sortKey = JSON.stringify(sortModel || []);
  const applySortToGrid = useCallback(() => {
    const api = gridApiRef.current;
    if (!server || !api) return;
    const current = (sortModel || [])[0];
    api.applyColumnState({
      state: current ? [{ colId: current.field, sort: current.sort }] : [],
      defaultState: { sort: null },
    });
  }, [server, sortModel]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(applySortToGrid, [sortKey, applySortToGrid]);

  const handleSortChanged = useCallback(
    (event) => {
      if (!server || !onSortModelChange) return;
      const sorted = event.api.getColumnState().find((c) => c.sort);
      const next = sorted ? [{ field: sorted.colId, sort: sorted.sort }] : [];
      // Ignore the echo of our own applyColumnState above.
      if (JSON.stringify(next) !== sortKey) onSortModelChange(next);
    },
    [server, onSortModelChange, sortKey]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      tooltipValueGetter: defaultTooltipValueGetter,
      ...(server ? { comparator: noopComparator } : {}),
    }),
    [server]
  );

  const serverPage = paginationModel?.page ?? 0;
  const serverPageSize = paginationModel?.pageSize ?? pageSize;
  // MUI warns if the current page size isn't one of the options (e.g. delivery's 58).
  const serverPageSizeOptions = pageSizeOptions.includes(serverPageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, serverPageSize].sort((a, b) => a - b);

  return (
    <div
      className={mode === 'dark' ? 'ag-theme-invotrix-dark' : 'ag-theme-invotrix-light'}
      style={{ width: '100%', ...sx }}
    >
      {inactiveRowStyles}
      <AgGridReact
        ref={setRefs}
        getRowClass={defaultGetRowClass}
        theme={mode === 'dark' ? agGridThemeDark : agGridThemeLight}
        rowData={rows}
        columnDefs={columns}
        loading={loading}
        pagination={!server}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={pageSizeOptions}
        domLayout={domLayout}
        getRowId={getRowId ? (params) => getRowId(params.data) : undefined}
        localeText={agGridLocaleFr}
        defaultColDef={defaultColDef}
        tooltipShowDelay={300}
        tooltipInteraction
        animateRows
        suppressCellFocus
        onGridReady={(event) => {
          gridApiRef.current = event.api;
          applySortToGrid();
          onGridReady?.(event);
        }}
        onSortChanged={(event) => {
          handleSortChanged(event);
          onSortChanged?.(event);
        }}
        {...rest}
      />
      {server && (
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <TablePagination
            component="div"
            count={rowCount}
            page={serverPage}
            rowsPerPage={serverPageSize}
            rowsPerPageOptions={serverPageSizeOptions}
            onPageChange={(_, newPage) =>
              onPaginationModelChange?.({ page: newPage, pageSize: serverPageSize })
            }
            onRowsPerPageChange={(e) =>
              onPaginationModelChange?.({ page: 0, pageSize: parseInt(e.target.value, 10) })
            }
            {...paginationLabels}
          />
        </Box>
      )}
    </div>
  );
});

export default AppDataGrid;
